import React, { useState, useEffect } from 'react';
import { getPilots, createPilot, updatePilot } from '../utils/supabaseQueries';
import type { Pilot, PilotInsert } from '../types/database';

const CrewManagement: React.FC = () => {
  const [pilots, setPilots] = useState<Pilot[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPilot, setEditingPilot] = useState<Pilot | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    licenseNumber: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    loadPilots();
  }, []);

  const loadPilots = async () => {
    try {
      setLoading(true);
      const data = await getPilots();
      setPilots(data);
    } catch (err) {
      console.error('Error loading pilots:', err);
      setError('Failed to load pilots');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPilot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const newPilot = await createPilot({
        name: formData.name.trim(),
        license_number: formData.licenseNumber.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
      });

      setPilots(prev => [...prev, newPilot].sort((a, b) => a.name.localeCompare(b.name)));
      setSuccess(`Pilot ${newPilot.name} added successfully!`);
      
      // Reset form
      setFormData({ name: '', licenseNumber: '', email: '', phone: '' });
      setShowAddForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add pilot');
    }
  };

  const handleEditPilot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPilot) return;

    setError(null);
    setSuccess(null);

    try {
      const updatedPilot = await updatePilot(editingPilot.id, {
        name: formData.name.trim(),
        license_number: formData.licenseNumber.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
      });

      setPilots(prev => prev.map(p => p.id === updatedPilot.id ? updatedPilot : p));
      setSuccess(`Pilot ${updatedPilot.name} updated successfully!`);
      setEditingPilot(null);
      setFormData({ name: '', licenseNumber: '', email: '', phone: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to update pilot');
    }
  };

  const startEdit = (pilot: Pilot) => {
    setEditingPilot(pilot);
    setFormData({
      name: pilot.name,
      licenseNumber: pilot.license_number || '',
      email: pilot.email || '',
      phone: pilot.phone || '',
    });
    setShowAddForm(false);
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingPilot(null);
    setFormData({ name: '', licenseNumber: '', email: '', phone: '' });
    setError(null);
  };

  const toggleAddForm = () => {
    setShowAddForm(!showAddForm);
    setEditingPilot(null);
    setFormData({ name: '', licenseNumber: '', email: '', phone: '' });
    setError(null);
    setSuccess(null);
  };

  const filteredPilots = pilots.filter(pilot =>
    pilot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pilot.license_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Crew Management</h2>
        <button
          onClick={toggleAddForm}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {showAddForm ? 'Cancel' : '+ Add New Pilot'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
          <p className="text-sm text-green-800">✓ {success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingPilot) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editingPilot ? 'Edit Pilot' : 'Add New Pilot'}
          </h3>
          <form onSubmit={editingPilot ? handleEditPilot : handleAddPilot} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Number
                </label>
                <input
                  type="text"
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, licenseNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., HP-1234"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., pilot@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., +255 123 456 789"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={editingPilot ? cancelEdit : toggleAddForm}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {editingPilot ? 'Update Pilot' : 'Add Pilot'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
          Search Pilots
        </label>
        <input
          type="text"
          id="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or license number..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Pilots List */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading pilots...</p>
        </div>
      ) : filteredPilots.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {searchTerm ? 'No pilots found matching your search.' : 'No pilots added yet. Click "+ Add New Pilot" to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  License Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPilots.map((pilot) => (
                <tr key={pilot.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{pilot.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{pilot.license_number || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{pilot.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{pilot.phone || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {pilot.active ? (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => startEdit(pilot)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Pilots:</span>
            <span className="ml-2 font-medium">{pilots.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Active Pilots:</span>
            <span className="ml-2 font-medium text-green-600">
              {pilots.filter(p => p.active).length}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Showing:</span>
            <span className="ml-2 font-medium">{filteredPilots.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrewManagement;

